import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility as VisibilityIcon, Download as DownloadIcon, Delete as DeleteIcon, Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { installationService, installationItemService } from '../services/api';
import * as XLSX from 'xlsx';

export default function Installations() {
  const [installations, setInstallations] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedInstallation, setSelectedInstallation] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // Item management for edit dialog
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [itemQuantity, setItemQuantity] = useState('');

  useEffect(() => {
    loadInstallations();
  }, []);

  const loadInstallations = async () => {
    try {
      const [installationsResponse, itemsResponse] = await Promise.all([
        installationService.getInstallations(),
        installationItemService.getItems(),
      ]);
      
      if (installationsResponse.success) {
        setInstallations(installationsResponse.data);
      }
      
      if (itemsResponse.success) {
        setAllItems(itemsResponse.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getMeterTypeLabel = (type) => {
    const labels = {
      residential: 'منزلي (Residential)',
      commercial: 'تجاري (Commercial)',
      agricultural: 'زراعي (Agricultural)',
      industrial: 'صناعي (Industrial)',
    };
    return labels[type] || type;
  };

  const getInstallationTypeLabel = (type) => {
    const labels = {
      buildings: 'عمارات (Buildings)',
      pole: 'بالو (Pole)',
      wall: 'جداري (Wall)',
      standalone: 'منسلة (Standalone)',
    };
    return labels[type] || type;
  };

  const columns = [
    {
      field: 'index',
      headerName: '#',
      width: 70,
      renderCell: (params) => (
        <strong>#{params.value}</strong>
      ),
    },
    {
      field: 'city',
      headerName: 'City',
      width: 110,
    },
    {
      field: 'district',
      headerName: 'District',
      width: 110,
    },
    {
      field: 'installer',
      headerName: 'Installer',
      width: 130,
      valueGetter: (params) => params.row.installer?.full_name || params.row.installer?.username || 'N/A',
    },
    {
      field: 'team',
      headerName: 'Team',
      width: 100,
      valueGetter: (params) => params.row.team?.name || 'N/A',
    },
    {
      field: 'barcode',
      headerName: 'Barcode',
      width: 140,
      renderCell: (params) => (
        <Typography variant='body2' sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'meter_type',
      headerName: 'Meter Type',
      width: 140,
      valueGetter: (params) => getMeterTypeLabel(params.value),
    },
    {
      field: 'new_meter_number',
      headerName: 'New Meter #',
      width: 120,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'completed' ? 'success' : 'warning'}
          size='small'
        />
      ),
    },
    {
      field: 'installation_date',
      headerName: 'Date',
      width: 110,
      valueGetter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            variant='outlined'
            size='small'
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewDetails(params.row)}
          >
            View
          </Button>
          <Button
            variant='outlined'
            size='small'
            color='primary'
            onClick={() => handleEditInstallation(params.row)}
          >
            Edit
          </Button>
          <Button
            variant='outlined'
            size='small'
            color='error'
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteInstallation(params.row)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  const filteredInstallations = installations.filter((inst) =>
    inst.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.district?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.new_meter_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.barcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.installer?.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /**
   * Handle view installation details
   */
  const handleViewDetails = (installation) => {
    setSelectedInstallation(installation);
    setDetailDialogOpen(true);
  };

  /**
   * Handle edit installation
   */
  const handleEditInstallation = (installation) => {
    setSelectedInstallation(installation);
    setEditFormData({
      installation_time: installation.installation_time || '',
      city: installation.city || '',
      district: installation.district || '',
      coordinates: {
        latitude: installation.coordinates?.latitude || '',
        longitude: installation.coordinates?.longitude || '',
      },
      meter_type: installation.meter_type || 'residential',
      installation_type: installation.installation_type || 'buildings',
      barcode: installation.barcode || '',
      old_meter_number: installation.old_meter_number || '',
      new_meter_number: installation.new_meter_number || '',
      notes: installation.notes || '',
      items_used: installation.items_used || [],
    });
    setEditDialogOpen(true);
    setError('');
  };

  /**
   * Add item to installation
   */
  const handleAddItem = () => {
    setSelectedItemId('');
    setItemQuantity('');
    setItemDialogOpen(true);
  };

  /**
   * Confirm add item
   */
  const handleConfirmAddItem = () => {
    if (!selectedItemId || !itemQuantity || parseInt(itemQuantity) <= 0) {
      alert('Please select an item and enter a valid quantity');
      return;
    }

    const item = allItems.find((i) => i._id === selectedItemId);
    if (!item) return;

    // Check if item already exists
    const existingIndex = editFormData.items_used.findIndex((i) => i.item === selectedItemId);
    
    if (existingIndex >= 0) {
      // Update quantity
      const updatedItems = [...editFormData.items_used];
      updatedItems[existingIndex].quantity = parseInt(itemQuantity);
      setEditFormData({ ...editFormData, items_used: updatedItems });
    } else {
      // Add new item
      setEditFormData({
        ...editFormData,
        items_used: [
          ...editFormData.items_used,
          {
            item: item._id,
            item_name: item.name,
            quantity: parseInt(itemQuantity),
            unit: item.unit,
          },
        ],
      });
    }

    setItemDialogOpen(false);
    setSelectedItemId('');
    setItemQuantity('');
  };

  /**
   * Remove item from installation
   */
  const handleRemoveItem = (index) => {
    const updatedItems = editFormData.items_used.filter((_, i) => i !== index);
    setEditFormData({ ...editFormData, items_used: updatedItems });
  };

  /**
   * Save edited installation
   */
  const handleSaveEdit = async () => {
    try {
      await installationService.updateInstallation(selectedInstallation._id, editFormData);
      setError('');
      alert('Installation updated successfully!');
      setEditDialogOpen(false);
      loadInstallations();
    } catch (err) {
      setError('Failed to update installation: ' + err.message);
    }
  };

  /**
   * Handle delete installation
   */
  const handleDeleteInstallation = async (installation) => {
    const confirmMessage = `Are you sure you want to delete installation #${installation.index}?\n\nCity: ${installation.city}\nBarcode: ${installation.barcode}\nInstaller: ${installation.installer?.full_name || installation.installer?.username}\n\nThis action will restore 1 meter credit to the worker.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await installationService.deleteInstallation(installation._id);
      setError(''); // Clear any previous errors
      alert(`Installation #${installation.index} deleted successfully. Worker's meter credit has been restored.`);
      loadInstallations();
    } catch (err) {
      setError('Failed to delete installation: ' + err.message);
    }
  };

  /**
   * Export installations to Excel
   */
  const handleExportToExcel = async () => {
    setExporting(true);
    
    try {
      // Prepare data for Excel
      const excelData = installations.map((inst) => {
        // Base installation data
        const row = {
          'تاريخ التركيب': new Date(inst.installation_date).toLocaleDateString('ar-SA'),
          'وقت التركيب': inst.installation_time || '',
          'المدينة': inst.city || '',
          'المنطقة': inst.district || '',
          'المقسم': inst.district || '',
          'قائد الفريق': inst.team?.leader?.full_name || inst.team?.leader?.username || '',
          'الفرقة': inst.team?.name || '',
          'اسم العضو': inst.installer?.full_name || inst.installer?.username || '',
          'نوع العداد': getMeterTypeLabel(inst.meter_type),
          'نوع التركيب': getInstallationTypeLabel(inst.installation_type),
          'الاندكس': inst.index || '',
          'العرض': inst.coordinates?.longitude || '',
          'الطول': inst.coordinates?.latitude || '',
          'الباركود': inst.barcode || '',
          'رقم العداد الجديد': inst.new_meter_number || '',
          'رقم العداد القديم': inst.old_meter_number || '',
          'ملاحظات': inst.notes || '',
        };

        // Add all items as columns
        allItems.forEach((item) => {
          const usedItem = inst.items_used?.find((ui) => ui.item_name === item.name);
          row[item.name] = usedItem ? usedItem.quantity : 0;
        });

        return row;
      });

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 15 }, // تاريخ التركيب
        { wch: 12 }, // وقت التركيب
        { wch: 15 }, // المدينة
        { wch: 15 }, // المنطقة
        { wch: 15 }, // المقسم
        { wch: 20 }, // قائد الفريق
        { wch: 15 }, // الفرقة
        { wch: 20 }, // اسم العضو
        { wch: 18 }, // نوع العداد
        { wch: 18 }, // نوع التركيب
        { wch: 10 }, // الاندكس
        { wch: 12 }, // العرض
        { wch: 12 }, // الطول
        { wch: 18 }, // الباركود
        { wch: 18 }, // رقم العداد الجديد
        { wch: 18 }, // رقم العداد القديم
        { wch: 30 }, // ملاحظات
      ];

      // Add widths for item columns
      allItems.forEach(() => {
        colWidths.push({ wch: 15 });
      });

      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'التركيبات');

      // Generate file name with current date
      const fileName = `installations_report_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download file
      XLSX.writeFile(wb, fileName);

      alert('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography variant='h4'>
          Installations Overview
        </Typography>
        <Button
          variant='contained'
          color='success'
          startIcon={exporting ? <CircularProgress size={20} color='inherit' /> : <DownloadIcon />}
          onClick={handleExportToExcel}
          disabled={exporting || installations.length === 0}
        >
          {exporting ? 'Exporting...' : 'Export to Excel'}
        </Button>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color='text.secondary' gutterBottom>
                Total Installations
              </Typography>
              <Typography variant='h4'>
                {installations.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color='text.secondary' gutterBottom>
                Completed
              </Typography>
              <Typography variant='h4' color='success.main'>
                {installations.filter((i) => i.status === 'completed').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color='text.secondary' gutterBottom>
                Today
              </Typography>
              <Typography variant='h4' color='primary.main'>
                {installations.filter((i) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const instDate = new Date(i.installation_date);
                  instDate.setHours(0, 0, 0, 0);
                  return instDate.getTime() === today.getTime();
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <TextField
            label='Search'
            placeholder='Search by city, district, barcode, meter number, or installer...'
            variant='outlined'
            fullWidth
            margin='normal'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
          />
          <DataGrid
            rows={filteredInstallations}
            columns={columns}
            getRowId={(row) => row._id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            autoHeight
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      {/* Edit Installation Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>تعديل التركيب #{selectedInstallation?.index}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Installation Time (وقت التركيب)'
                value={editFormData.installation_time || ''}
                onChange={(e) => setEditFormData({ ...editFormData, installation_time: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Barcode (الباركود)'
                value={editFormData.barcode || ''}
                onChange={(e) => setEditFormData({ ...editFormData, barcode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='City (المدينة)'
                value={editFormData.city || ''}
                onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='District (المقسم)'
                value={editFormData.district || ''}
                onChange={(e) => setEditFormData({ ...editFormData, district: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Latitude (العرض)'
                value={editFormData.coordinates?.latitude || ''}
                onChange={(e) => setEditFormData({ 
                  ...editFormData, 
                  coordinates: { ...editFormData.coordinates, latitude: parseFloat(e.target.value) || '' }
                })}
                type='number'
                inputProps={{ step: '0.000001' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Longitude (الطول)'
                value={editFormData.coordinates?.longitude || ''}
                onChange={(e) => setEditFormData({ 
                  ...editFormData, 
                  coordinates: { ...editFormData.coordinates, longitude: parseFloat(e.target.value) || '' }
                })}
                type='number'
                inputProps={{ step: '0.000001' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Meter Type (نوع العداد)</InputLabel>
                <Select
                  value={editFormData.meter_type || 'residential'}
                  label='Meter Type (نوع العداد)'
                  onChange={(e) => setEditFormData({ ...editFormData, meter_type: e.target.value })}
                >
                  <MenuItem value='residential'>منزلي (Residential)</MenuItem>
                  <MenuItem value='commercial'>تجاري (Commercial)</MenuItem>
                  <MenuItem value='agricultural'>زراعي (Agricultural)</MenuItem>
                  <MenuItem value='industrial'>صناعي (Industrial)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Installation Type (نوع التركيب)</InputLabel>
                <Select
                  value={editFormData.installation_type || 'buildings'}
                  label='Installation Type (نوع التركيب)'
                  onChange={(e) => setEditFormData({ ...editFormData, installation_type: e.target.value })}
                >
                  <MenuItem value='buildings'>عمارات (Buildings)</MenuItem>
                  <MenuItem value='pole'>بالو (Pole)</MenuItem>
                  <MenuItem value='wall'>جداري (Wall)</MenuItem>
                  <MenuItem value='standalone'>منسلة (Standalone)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Old Meter Number (رقم العداد القديم)'
                value={editFormData.old_meter_number || ''}
                onChange={(e) => setEditFormData({ ...editFormData, old_meter_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='New Meter Number (رقم العداد الجديد)'
                value={editFormData.new_meter_number || ''}
                onChange={(e) => setEditFormData({ ...editFormData, new_meter_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Notes (ملاحظات)'
                value={editFormData.notes || ''}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          {/* Items Used Section */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant='h6'>Materials Used (المواد المستخدمة)</Typography>
              <Button
                startIcon={<AddIcon />}
                variant='outlined'
                size='small'
                onClick={handleAddItem}
              >
                Add Item
              </Button>
            </Box>

            {editFormData.items_used && editFormData.items_used.length > 0 ? (
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Item Name</strong></TableCell>
                      <TableCell align='center'><strong>Quantity</strong></TableCell>
                      <TableCell align='center'><strong>Unit</strong></TableCell>
                      <TableCell align='center'><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editFormData.items_used.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.item_name}</TableCell>
                        <TableCell align='center'>{item.quantity}</TableCell>
                        <TableCell align='center'>{item.unit}</TableCell>
                        <TableCell align='center'>
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() => handleRemoveItem(index)}
                            title='Remove Item'
                          >
                            <RemoveIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity='info'>No items added yet. Click "Add Item" to add materials.</Alert>
            )}
          </Box>

          <Alert severity='warning' sx={{ mt: 2 }}>
            <strong>Note:</strong> Images cannot be changed via dashboard. All other fields including items can be updated.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant='contained' color='primary'>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={itemDialogOpen} onClose={() => setItemDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Add Material</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Select Item</InputLabel>
            <Select
              value={selectedItemId}
              label='Select Item'
              onChange={(e) => setSelectedItemId(e.target.value)}
            >
              <MenuItem value=''>
                <em>-- Select an Item --</em>
              </MenuItem>
              {allItems
                .filter((item) => item.is_active)
                .filter((item) => 
                  item.installation_type === 'all' || 
                  item.installation_type === editFormData.installation_type
                )
                .map((item) => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.name} ({item.unit})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label='Quantity'
            type='number'
            value={itemQuantity}
            onChange={(e) => setItemQuantity(e.target.value)}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmAddItem} variant='contained' color='primary'>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Installation Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          Installation Details - #{selectedInstallation?.index}
        </DialogTitle>
        <DialogContent>
          {selectedInstallation && (
            <Box sx={{ pt: 2 }}>
              {/* Location Information */}
              <Typography variant='h6' gutterBottom color='primary'>
                Location Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    City
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {selectedInstallation.city}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    District
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {selectedInstallation.district}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Latitude
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {selectedInstallation.coordinates?.latitude}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Longitude
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {selectedInstallation.coordinates?.longitude}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Installation Information */}
              <Typography variant='h6' gutterBottom color='primary'>
                Installation Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Installation Date
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {new Date(selectedInstallation.installation_date).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Installation Time
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {selectedInstallation.installation_time}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Meter Type
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {getMeterTypeLabel(selectedInstallation.meter_type)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Installation Type
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {getInstallationTypeLabel(selectedInstallation.installation_type)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Meter Information */}
              <Typography variant='h6' gutterBottom color='primary'>
                Meter Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Typography variant='body2' color='text.secondary'>
                    Barcode
                  </Typography>
                  <Typography variant='body1' fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                    {selectedInstallation.barcode}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Old Meter Number
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {selectedInstallation.old_meter_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    New Meter Number
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {selectedInstallation.new_meter_number}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Worker Information */}
              <Typography variant='h6' gutterBottom color='primary'>
                Worker Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Installer
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {selectedInstallation.installer?.full_name || selectedInstallation.installer?.username}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Team
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {selectedInstallation.team?.name || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Items Used */}
              {selectedInstallation.items_used && selectedInstallation.items_used.length > 0 ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant='h6' gutterBottom color='primary'>
                    📦 Materials Used ({selectedInstallation.items_used.length} items)
                  </Typography>
                  <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2 }}>
                    <Grid container spacing={1.5}>
                      {selectedInstallation.items_used.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: 'white',
                              borderRadius: 1,
                              border: '1px solid #e0e0e0',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Box>
                              <Typography variant='body1' fontWeight={600} color='text.primary'>
                                {item.item_name}
                              </Typography>
                              <Typography variant='caption' color='text.secondary'>
                                {item.unit}
                              </Typography>
                            </Box>
                            <Chip
                              label={item.quantity}
                              color='primary'
                              size='medium'
                              sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </>
              ) : (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant='h6' gutterBottom color='primary'>
                    📦 Materials Used
                  </Typography>
                  <Alert severity='info' sx={{ mt: 1 }}>
                    No materials were recorded for this installation
                  </Alert>
                </>
              )}

              {/* Notes */}
              {selectedInstallation.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant='h6' gutterBottom color='primary'>
                    Notes
                  </Typography>
                  <Typography variant='body1' sx={{ mb: 2 }}>
                    {selectedInstallation.notes}
                  </Typography>
                </>
              )}

              {/* Image */}
              {selectedInstallation.image && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant='h6' gutterBottom color='primary'>
                    Image
                  </Typography>
                  <Box
                    component='img'
                    src={selectedInstallation.image}
                    alt='Installation'
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 400,
                      borderRadius: 2,
                      mt: 1,
                    }}
                  />
                </>
              )}

              {/* Status */}
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Status
                  </Typography>
                  <Chip
                    label={selectedInstallation.status}
                    color={selectedInstallation.status === 'completed' ? 'success' : 'warning'}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Created At
                  </Typography>
                  <Typography variant='body1' fontWeight={500}>
                    {new Date(selectedInstallation.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

